import { HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { api, type RegistryCommandState } from '../../api';
import type { RegistryObjectType, RegistryTransactionQuery, TrafficClass } from '../../api/areas/registry';
import { FilterBar, Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useApi } from '../../lib/useApi';
import { COMMAND_STATE } from '../domains/status';
import {
  enumLabel,
  FilterSelect,
  Pager,
  RefreshButton,
  RegistrySelect,
  ResetButton,
  TextFilter,
  TransactionTable,
  UnknownOutcomeCallout,
  useParamSetter,
  useRegistryCrumbs,
} from './shared';

const OBJECT_TYPES: RegistryObjectType[] = ['DOMAIN', 'CONTACT', 'HOST', 'SESSION', 'POLL'];
const TRAFFIC_CLASSES: TrafficClass[] = ['INTERACTIVE', 'BACKGROUND', 'POLL', 'DROPCATCH'];
const FILTER_KEYS = ['registry', 'operation', 'state', 'object_type', 'traffic_class', 'object_key', 'trid'];

/** `/registry/transactions`: every EPP command with clTRID / svTRID and outcome, newest first (spec §28, ADR-008). */
export function TransactionsPage() {
  const { t } = useTranslation('registry');
  useDocumentTitle(t('transactions.title'));
  const crumbs = useRegistryCrumbs();
  const [params, set, reset] = useParamSetter();
  const q: RegistryTransactionQuery = {
    registry: params.get('registry') || undefined,
    operation: params.get('operation') || undefined,
    state: (params.get('state') || undefined) as RegistryTransactionQuery['state'],
    object_type: (params.get('object_type') || undefined) as RegistryObjectType | undefined,
    traffic_class: (params.get('traffic_class') || undefined) as TrafficClass | undefined,
    object_key: params.get('object_key') || undefined,
    trid: params.get('trid') || undefined,
    before: params.get('before') || undefined,
    limit: 50,
  };
  const result = useApi((signal) => api.listRegistryTransactions(q, { signal }), [params.toString()]);
  const filtered = FILTER_KEYS.some((k) => params.get(k));
  const unknownOnly = q.state === 'UNKNOWN' || q.state === 'UNRESOLVED';

  return (
    <>
      <PageHeader
        title={t('transactions.title')}
        crumbs={crumbs}
        subtitle={t('transactions.subtitle')}
        actions={
          <>
            <Button type="button" variant={unknownOnly ? 'secondary' : 'outline'} size="sm" aria-pressed={q.state === 'UNRESOLVED'} onClick={() => set('state', q.state === 'UNRESOLVED' ? '' : 'UNRESOLVED')}>
              <HelpCircle /> {t('transactions.onlyUnknown')}
            </Button>
            <RefreshButton onClick={result.reload} />
          </>
        }
      />
      <div className="space-y-4">
        <UnknownOutcomeCallout tone={unknownOnly ? 'serious' : 'info'} />
        <FilterBar actions={<ResetButton onClick={reset} disabled={!filtered} />}>
          <RegistrySelect value={q.registry ?? ''} onChange={(v) => set('registry', v)} />
          <FilterSelect
            label={t('filter.outcome')}
            value={q.state ?? ''}
            onChange={(v) => set('state', v)}
            className="min-w-52"
            options={[
              { value: 'UNRESOLVED', label: enumLabel('commandState', 'UNRESOLVED') },
              ...(Object.keys(COMMAND_STATE) as RegistryCommandState[]).map((s) => ({ value: s, label: enumLabel('commandState', s) })),
            ]}
          />
          <FilterSelect
            label={t('filter.object')}
            value={q.object_type ?? ''}
            onChange={(v) => set('object_type', v)}
            options={OBJECT_TYPES.map((o) => ({ value: o, label: enumLabel('objectType', o) }))}
          />
          <FilterSelect
            label={t('filter.trafficClass')}
            value={q.traffic_class ?? ''}
            onChange={(v) => set('traffic_class', v)}
            options={TRAFFIC_CLASSES.map((c) => ({ value: c, label: enumLabel('trafficClass', c) }))}
          />
          <TextFilter label={t('filter.command')} value={q.operation ?? ''} placeholder={t('filter.commandPlaceholder')} onApply={(v) => set('operation', v)} />
          <TextFilter label={t('filter.objectKey')} value={q.object_key ?? ''} placeholder={t('filter.objectKeyPlaceholder')} onApply={(v) => set('object_key', v)} />
          <TextFilter label={t('filter.trid')} value={q.trid ?? ''} placeholder={t('filter.tridPlaceholder')} onApply={(v) => set('trid', v)} />
        </FilterBar>
        <Section
          flush
          title={t('transactions.listTitle')}
          description={result.status === 'success' ? t('transactions.count', { count: result.data.items.length }) : undefined}
        >
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading rows={6} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} title={t('transactions.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' && result.data.items.length === 0 ? (
            <div className="px-5 pb-5">
              <EmptyState title={t('transactions.empty')} />
            </div>
          ) : null}
          {result.status === 'success' && result.data.items.length > 0 ? (
            <>
              <TransactionTable rows={result.data.items} />
              <Pager next={result.data.next_before} onNext={(c) => set('before', c)} first={!q.before} onFirst={() => set('before', '')} />
            </>
          ) : null}
        </Section>
      </div>
    </>
  );
}
